import React from 'react';
import { Box, Text, Flex, forwardRef } from '@chakra-ui/react';
import { useColorMode } from '@allergan-data-labs/alle-elements-core';
import {
  getChipStyles,
  getDisabledStyles,
  getChipTextStyles,
} from './chip.theme';
import type { ChipProps, ChipGroupProps } from './chip-types';
import { ChipIcon } from './chip-icon';
import { ChipCloseButton } from './chip-close-button';

/**
 * Chips are interactive elements that help users enter information, make selections, filter content, or trigger actions.
 */
export const Chip = forwardRef<ChipProps, 'button'>(
  (
    {
      text,
      icon,
      colorScheme = 'brand',
      colorMode: propColorMode,
      size = 'sm',
      onClick,
      isDisabled = false,
      onClose,
      ...rest
    },
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);

    const performClickAction = () => {
      if (!isDisabled && onClick) {
        onClick();
      }
    };

    const handleOnClick = (event: React.MouseEvent) => {
      if (!isDisabled && onClick) {
        onClick(event);
      }
    };

    const handleOnClose = () => {
      if (!isDisabled && onClose) {
        onClose({
          text,
          icon,
          colorScheme,
          colorMode,
          size,
          onClick,
          isDisabled,
          onClose,
        });
      }
    };

    const handleKeydown = (event: React.KeyboardEvent) => {
      const SPACE_KEY_CODE = 'Space';
      const ENTER_KEY_CODE = 'Enter';
      const key = event.key || event.code;
      if (key === SPACE_KEY_CODE || key === ENTER_KEY_CODE) {
        event.preventDefault();
        performClickAction();
      }
    };

    return (
      <Box
        ref={ref}
        role="button"
        sx={{
          ...getChipStyles(colorScheme, colorMode, size),
          ...(isDisabled && getDisabledStyles(colorMode)),
        }}
        onClick={handleOnClick}
        onKeyDown={handleKeydown}
        tabIndex={!isDisabled && colorScheme !== 'error' ? 0 : -1}
        {...rest}
      >
        {icon && <ChipIcon icon={icon} />}

        <Text sx={getChipTextStyles()} as="span">
          {text}
        </Text>
        {onClose && (
          <ChipCloseButton
            colorScheme={colorScheme}
            colorMode={colorMode}
            onClose={handleOnClose}
            isDisabled={isDisabled}
          />
        )}
      </Box>
    );
  }
);

export const ChipGroup = forwardRef<ChipGroupProps, 'div'>(
  ({ chips = [], onCloseClick, ...props }, ref) => {
    return (
      <Flex ref={ref} wrap="wrap" rowGap={8} gridGap={8} {...props}>
        {chips.map((chip, index) => (
          <Chip key={index} onClose={onCloseClick} {...chip} />
        ))}
      </Flex>
    );
  }
);
